import { httpClient } from "@/lib/http";
import type { ApiResponse } from "@/types/api";

export type ApiDimension = {
	description: string;
	id: string;
	name: string;
	shortName: string;
	sortOrder: number;
	userExplanation: string;
};

export type ApiSubskill = {
	definition: string;
	dimensionId: string;
	id: string;
	name: string;
	sortOrder: number;
};

export type ApiEvidenceRule = {
	dimensionId: string;
	exerciseId: string | null;
	id: string;
	ruleType: "score" | "no_score";
	sortOrder: number;
	text: string;
};

export type ApiPractice = {
	actionStepsJson?: string;
	actionStrategy: string;
	appSurface: string;
	author: string;
	difficultyTag: string;
	dimensionId: string;
	estimatedMinutes: number;
	formTag: string;
	id: string;
	isNative: number;
	isShareable: number;
	originalTheme: string;
	recordPrompt?: string;
	socialTag: string;
	sourceOrder: number;
	theorySource: string;
	title: string;
};

export type ApiTimelineItem = {
	authorName?: string;
	content: string;
	createdAt: string;
	dimensionId?: string;
	exerciseId?: string;
	feedbackScore?: number | null;
	friendName?: string | null;
	friendTag?: string | null;
	id: string;
	image: string | null;
	imageKey: string | null;
	kind: "practice" | "note";
	mode: string;
	reactions?: {
		seen: number;
		support: number;
	};
	summaryKeyword?: string;
	targetGroupId?: string | null;
	targetGroupName?: string | null;
	text: string;
	time: string;
	title?: string;
	userId?: string;
	visibility?: "private" | "group" | "friend";
	voice: number;
	voiceSeconds: number;
};

export type ApiRelationAsset = {
	createdAt: string;
	id: string;
	name: string;
	sourceKind: "practice" | "note";
	sourceNoteId: string | null;
	sourceRecordId: string | null;
	sourceTitle: string;
	tag: string;
	visibility: string;
};

export type ApiBadge = {
	description: string;
	dimensionId: string;
	icon: string;
	id: string;
	name: string;
	sortOrder: number;
	unlockRule: string;
	unlockedAt: string | null;
};

export type ApiProgramDay = {
	dayNumber: number;
	exerciseId: string | null;
	focusText: string;
	id: string;
	programId: string;
	title: string;
};

export type ApiProgram = {
	completedDays: number;
	currentDay: number;
	days: ApiProgramDay[];
	description: string;
	dimensionId: string;
	id: string;
	rewardText: string;
	sortOrder: number;
	status: string;
	title: string;
	totalDays: number;
};

export type ApiGroup = {
	description: string;
	dimensionId: string | null;
	id: string;
	joined: boolean;
	memberCount: number;
	name: string;
	session: {
		dimensionId: string | null;
		groupId: string;
		id: string;
		scheduledLabel: string;
		title: string;
	} | null;
	sortOrder: number;
};

export type ApiGrowthReport = {
	focusDimension: string;
	headline: string;
	nextDecision: string;
	ranges: Array<{
		days: number;
		dimensions: Array<{ count: number; dimensionId: string }>;
		total: number;
	}>;
};

export type ApiEncouragement = {
	id: string;
	scene: string;
	sortOrder: number;
	text: string;
	tone: string;
};

export type ApiTodayDraw = {
	availableMinutes: number;
	completed: boolean;
	completedAt: string | null;
	createdAt: string;
	dateKey: string;
	id: string;
	openingLine: string;
	practice: ApiPractice;
	reason: string;
	recommendedDimension: string;
	recordId: string | null;
};

export type ApiOngoingPractice = {
	checked: boolean;
	days: number;
	exerciseId: string;
	id: string;
	startedAt: string;
	status: string;
	todayIndex: number;
	updatedAt: string;
	week: number[];
};

export type GrowthBootstrap = {
	badges: ApiBadge[];
	dailyEncouragement: string;
	dimensions: ApiDimension[];
	encouragements: ApiEncouragement[];
	evidenceRules: ApiEvidenceRule[];
	feedbacks: Array<{
		createdAt: string;
		exerciseId: string | null;
		id: string;
		nextAction: string;
		reason: string;
		recordId: string | null;
		score: number;
	}>;
	groupTimeline: ApiTimelineItem[];
	groups: ApiGroup[];
	ongoing: ApiOngoingPractice[];
	practices: ApiPractice[];
	profile: {
		joined: string;
		mediaCount: number;
		notesCount: number;
		steadyDays: number;
		todayCompleted: number;
		totalPractice: number;
		wellbeing: number;
	};
	programs: ApiProgram[];
	relations: ApiRelationAsset[];
	report: ApiGrowthReport;
	stats: Array<{ count: number; dimensionId: string }>;
	subskills: ApiSubskill[];
	todayDraw: ApiTodayDraw | null;
	timeline: ApiTimelineItem[];
};

export type GrowthDraw = {
	alreadyDrawn: boolean;
	completed: boolean;
	practice: ApiPractice;
	reason: string;
	recommendedDimension: string;
	todayDraw: ApiTodayDraw;
};

export type PracticeRecordRequest = {
	content: string;
	dateKey?: string;
	exerciseId: string;
	feedbackScore?: number;
	imageKey?: string | null;
	mode: "text" | "image" | "voice";
	summaryKeyword?: string;
	voiceSeconds?: number;
};

export type PracticeRecordResponse = {
	badges: ApiBadge[];
	evidenceScore: number;
	id: string;
	nextAction: string;
	nextSuggestion: string;
	programProgress: {
		completedDays: number;
		currentDay: number;
		id: string;
		programId: string;
		status: string;
		totalDays: number;
	} | null;
};

export type NoteRequest = {
	authorName?: string;
	content: string;
	friendName?: string;
	friendTag?: string;
	imageKey?: string | null;
	mode: "text" | "image" | "voice";
	targetGroupId?: string | null;
	visibility?: "private" | "group" | "friend";
	voiceSeconds?: number;
};

export type FeedbackRequest = {
	exerciseId?: string;
	reason?: string;
	recordId?: string;
	score: number;
};

export type FeedbackResponse = {
	id: string;
	nextAction: string;
	nextSuggestion: string;
	score: number;
};

export type RelationAssetRequest = {
	name: string;
	sourceKind?: "practice" | "note";
	sourceNoteId?: string | null;
	sourceRecordId?: string | null;
	sourceTitle?: string;
	tag: string;
	visibility?: string;
};

export type GroupReactionResponse = {
	noteId: string;
	reactionType: "seen" | "support";
	reactions: {
		seen: number;
		support: number;
	};
};

export const growthBootstrapKey = "/growth/bootstrap";

export const getClientDateKey = () => {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const day = String(now.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

export function getGrowthBootstrap(): Promise<ApiResponse<GrowthBootstrap>> {
	return httpClient.get(growthBootstrapKey, { params: { dateKey: getClientDateKey() } });
}

export function drawGrowthPractice(data: {
	availableMinutes?: number;
	openingLine: string;
}): Promise<ApiResponse<GrowthDraw>> {
	return httpClient.post("/growth/draw", { ...data, dateKey: getClientDateKey() });
}

export function createPracticeRecord(
	data: PracticeRecordRequest,
): Promise<ApiResponse<PracticeRecordResponse>> {
	return httpClient.post("/growth/practice-records", { ...data, dateKey: getClientDateKey() });
}

export function toggleOngoingPractice(data: { exerciseId: string }): Promise<
	ApiResponse<{
		active: boolean;
		exerciseId: string;
		id: string;
	}>
> {
	return httpClient.post("/growth/ongoing", data);
}

export function createNote(
	data: NoteRequest,
): Promise<
	ApiResponse<{ badges: ApiBadge[]; id: string; relationAsset: ApiRelationAsset | null }>
> {
	return httpClient.post("/growth/notes", data);
}

export function createPracticeFeedback(
	data: FeedbackRequest,
): Promise<ApiResponse<FeedbackResponse>> {
	return httpClient.post("/growth/feedback", data);
}

export function createRelationAsset(
	data: RelationAssetRequest,
): Promise<ApiResponse<{ asset: ApiRelationAsset; badges: ApiBadge[] }>> {
	return httpClient.post("/growth/relation-assets", data);
}

export function createGroupReaction(data: {
	noteId: string;
	reactionType: "seen" | "support";
}): Promise<ApiResponse<GroupReactionResponse>> {
	return httpClient.post("/growth/group-reactions", data);
}

export function joinGrowthGroup(data: { groupId: string }): Promise<
	ApiResponse<{
		groupId: string;
		joined: boolean;
		name: string;
	}>
> {
	return httpClient.post("/growth/group-members", data);
}
